import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PunchOverrideReason, PunchType, dayKeyInTimeZone, distanceMeters } from '@construction/shared';
import { attendanceApi, type Site } from '@/api/endpoints';
import { Alert, Badge, Button, Card, EmptyState, ListGroup, ListRow, PageHeader, Spinner } from '@/components/ui';
import { QueueBar, enqueue } from '@/features/upload-queue';
import { getCurrentFix, isOnline, LocationError, type Fix } from '@/native';
import { useErrorMessage } from '@/hooks/useErrorMessage';
import { useMobileSite } from '@/hooks/useCurrentSite';
import { useSiteLock, writeLocalPunch } from '@/hooks/useSiteLock';
import { ContinuePunchSheet } from './ContinuePunchSheet';
import { useAuthStore } from '@/store/auth';

export function PunchPage() {
  const { t, i18n } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const scheduleClockOutLogout = useAuthStore((s) => s.scheduleClockOutLogout);
  const queryClient = useQueryClient();
  const toMessage = useErrorMessage();

  // 工地在首页选，这里只跟随（CR-007/CR-008）—— 打卡页不再自己挑工地。
  // 在班期间它是锁死的（CR-015）。
  const { site, siteId, isLoading } = useMobileSite();
  const { shift, open: onTheClock, startedAt, pendingSync, clockSkewMs } = useSiteLock();
  const [fix, setFix] = useState<Fix | null>(null);
  const [locating, setLocating] = useState(false);
  /** 定位失败或超范围时弹出的「继续打卡」面板 */
  const [continueKind, setContinueKind] = useState<'NO_LOCATION' | 'OUT_OF_RANGE' | null>(null);
  const [message, setMessage] = useState<{ tone: 'success' | 'error' | 'info'; text: string } | null>(
    null,
  );

  // 提示自动消失。否则「当前无网络」会一直挂在那里，
  // 工人走到有信号的地方、队列已经传完了，屏幕上还写着没网络。
  useEffect(() => {
    if (!message) return;
    const timer = window.setTimeout(() => setMessage(null), 6000);
    return () => window.clearTimeout(timer);
  }, [message]);

  // 进页面就先取一次定位：GPS 冷启动要十几秒，等工人点了按钮再取会干等
  useEffect(() => {
    if (!siteId) return;
    void refreshLocation();
    // 只在切换工地时重新定位；refreshLocation 是稳定的局部函数，不进依赖
  }, [siteId]);

  async function refreshLocation() {
    setLocating(true);
    try {
      setFix(await getCurrentFix());
    } catch (error) {
      setFix(null);
      setMessage({
        tone: 'error',
        text:
          error instanceof LocationError && error.kind === 'DENIED'
            ? t('errors.PUNCH_LOCATION_REQUIRED')
            : t('punch.locateFailed'),
      });
    } finally {
      setLocating(false);
    }
  }

  /** 前端算的距离只用于显示。是否放行由服务端判定，改前端代码伪造不了。 */
  const distance = useMemo(() => {
    if (!fix || !site) return null;
    return distanceMeters({ lat: fix.lat, lng: fix.lng }, { lat: site.lat, lng: site.lng });
  }, [fix, site]);

  const inRange = distance != null && site != null && distance - (fix?.accuracyM ?? 0) <= site.geofenceRadiusM;

  const punch = useMutation({
    mutationFn: async ({
      type,
      override,
    }: {
      type: PunchType;
      override?: { reason: PunchOverrideReason; note: string };
    }) => {
      if (!site || !user) throw new LocationError('UNAVAILABLE');
      // 没有定位时必须带理由 —— 不然服务端也会拒
      if (!fix && !override) throw new LocationError('UNAVAILABLE');

      const online = await isOnline();
      // 打卡时刻只取一次：本地记录和上报的 payload 必须是同一个值，
      // 各自 new Date() 会差几毫秒，而「服务端追上来了没有」正是按这个时刻比的
      const occurredAt = new Date().toISOString();

      // 不管在不在线都先入队：入队即落盘，工人立刻得到确认，
      // 有网时队列会在几百毫秒内传完
      const clientId = await enqueue({
        kind: 'PUNCH',
        userId: user.id,
        siteId: site.id,
        payload: {
          type,
          occurredAt,
          // 统一用 null 而不是 undefined：IndexedDB 存得住，
          // 而 undefined 分不清「没定位」和「忘了传」
          lat: fix?.lat ?? null,
          lng: fix?.lng ?? null,
          accuracyM: fix?.accuracyM ?? undefined,
          isMocked: fix?.isMocked ?? false,
          overrideReason: override?.reason,
          overrideReasonNote: override?.note || undefined,
        },
      });

      // **上班卡和下班卡都要记**。服务端这会儿还不知道有这条打卡
      // （尤其离线时它还躺在队列里）：
      // - 不记上班卡 → 按钮还显示「上班打卡」，工人会再点一次，
      //   服务端收到两条 IN，第二条判成 DUPLICATE_IN 进待复核（客户实测到的）；
      // - 不记下班卡 → 服务端还以为在班，工地一直锁着，直到队列传完。
      writeLocalPunch(user.id, {
        siteId: site.id,
        type,
        occurredAt,
        clientId,
      });

      return { online, type };
    },
    onSuccess: async ({ online, type }) => {
      // 下班打卡 = 这个人今天用完了这台手机（CR-006）。
      // 但不能在这里直接退出：打卡是先入队再上传的，退出会清掉凭证，
      // 队列里那条打卡就永远传不上去了。所以只登记待退出，
      // 由 store 等队列传完、且服务端没判成「超范围拒绝」之后再退。
      if (type === PunchType.OUT) {
        scheduleClockOutLogout();
      }
      setContinueKind(null);

      setMessage({
        tone: online ? 'success' : 'info',
        text: online
          ? t(type === PunchType.IN ? 'punch.clockedIn' : 'punch.clockedOut')
          : t(type === PunchType.OUT ? 'auth.clockOutPendingUpload' : 'punch.queuedOffline'),
      });
      // **不在这里 invalidate `me-shift`**：那张卡还在队列里，
      // 这会儿去问服务端只会拿回「没在班」，而且把缓存刷成 fresh，
      // 接下来 60 秒都不再自动刷 —— 界面反而卡在错的状态上更久。
      // 队列传完之后由 `App.tsx` 里的上传回调统一刷新，见那里的注释。
      // 今日记录列表同理，晚一点刷。
      window.setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: ['punches-today', siteId] });
      }, 1500);
    },
    onError: (error) => setMessage({ tone: 'error', text: toMessage(error) }),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-20 text-ink-3">
        <Spinner />
      </div>
    );
  }

  if (!site) {
    return <EmptyState title={t('site.empty')} hint={t('site.emptyHintWorker')} />;
  }

  // 下一步是上班还是下班，**以班次状态为准**（CR-015），
  // 而且那个状态**已经把还在队列里的那张卡算进去了**。
  //
  // 不能用「今日打卡」：它按工地时区的自然日截断，跨夜班的次日 00:01
  // 查不到前一晚那条上班卡，于是首页显示「上班打卡」，而人明明还在班上。
  // 也不能只看服务端：打卡是先入队再上传的，按下按钮那一刻它还不知道 ——
  // 客户实测到的连打两次上班卡就是这么来的。
  const nextAction = onTheClock ? PunchType.OUT : PunchType.IN;
  const isClockIn = nextAction === PunchType.IN;

  return (
    <>
      {/* 页头和别的页一样（CR-082）：标题是「打卡」，下面是谁在打 —— 手机多人轮换，打之前看一眼是不是自己（CR-006） */}
      <PageHeader title={t('nav.punch')} description={user?.name} />

      {onTheClock && startedAt && (
        <ShiftTimer
          startedAt={startedAt}
          clockSkewMs={clockSkewMs}
          pendingSync={pendingSync}
        />
      )}

      {shift?.stale && shift.punch && (
        <div className="mb-4">
          <Alert tone="info">
            {t('punch.staleShift', {
              date: new Date(shift.punch.occurredAt).toLocaleString(i18n.language, {
                month: 'numeric',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              }),
            })}
          </Alert>
        </div>
      )}

      <div className="mb-4">
        <QueueBar />
      </div>

      {message && (
        <div className="mb-4">
          <Alert tone={message.tone === 'error' ? 'error' : message.tone === 'success' ? 'success' : 'info'}>
            {message.text}
          </Alert>
        </div>
      )}

      <LocationStatus
        site={site}
        fix={fix}
        distance={distance}
        inRange={inRange}
        locating={locating}
        onRefresh={() => void refreshLocation()}
      />

      {/* 打卡按钮做得很大：工人可能戴着手套，屏幕上有灰，一只手拿着工具 */}
      {/*
        没拿到定位、或人在围栏外时，大按钮变灰点不了（CR-046，客户原话「打卡按键是灰色的不能点击」），
        下面多一个小的「强行打卡」，点了进「打不上卡？说明原因后继续」—— 照样能打，只是进待复核。
        「强行打卡」这条路不能拿掉：CR-015 之前拿不到定位就根本打不了卡，室内、地下室是工地上的日常。
      */}
      <button
        type="button"
        disabled={punch.isPending || !fix || !inRange}
        onClick={() => punch.mutate({ type: nextAction })}
        data-action="punch"
        className={[
          'mt-5 flex min-h-40 w-full flex-col items-center justify-center gap-2 rounded-xl',
          'text-figure font-semibold text-white transition-colors',
          'disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-ink-3',
          isClockIn ? 'bg-brand-600 active:bg-brand-700' : 'bg-safety-500 active:bg-safety-600',
        ].join(' ')}
      >
        {punch.isPending ? (
          <Spinner size={32} />
        ) : (
          <>
            <span>{isClockIn ? t('punch.clockIn') : t('punch.clockOut')}</span>
            <span className="text-body font-normal opacity-85">
              {new Date().toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}
            </span>
          </>
        )}
      </button>

      {/* 定位进行中不给「强行打卡」：GPS 冷启动要十几秒，等结果出来再决定 */}
      {!inRange && !locating && (
        <>
          <p className="mt-3 text-center text-body text-danger">
            {fix && distance != null
              ? t('punch.outOfRangeHint', {
                  distance: Math.round(distance),
                  allowed: site.geofenceRadiusM,
                })
              : t('punch.noLocationHint')}
          </p>
          <div className="mt-3 flex justify-center">
            <Button
              variant="secondary"
              size="glove"
              disabled={punch.isPending}
              onClick={() => setContinueKind(fix ? 'OUT_OF_RANGE' : 'NO_LOCATION')}
              data-action="force-punch"
            >
              {t('punch.forcePunch')}
            </Button>
          </div>
        </>
      )}

      {siteId !== null && <TodayList siteId={siteId} />}

      {continueKind && site && (
        <ContinuePunchSheet
          kind={continueKind}
          distanceM={distance}
          allowedM={site.geofenceRadiusM}
          submitting={punch.isPending}
          onCancel={() => setContinueKind(null)}
          onConfirm={(reason, note) =>
            punch.mutate({ type: nextAction, override: { reason, note } })
          }
        />
      )}
    </>
  );
}

/**
 * 已工作时长。
 *
 * **每次都从时间戳重算，绝不做计数器累加** —— 安卓 WebView 切到后台会节流
 * 甚至冻结定时器，累加必然走慢，而工人盯着的就是这个数字。
 *
 * 基准用服务端时刻加一次时钟差：打卡本来就为「设备时钟不准」设了校验，
 * 手机时间差半小时的话，直接用本地时间会显示一个离谱的数字。
 *
 * **时钟差是收到响应那一刻量的一个常数**（见 `useSiteLock`）。
 * 早先它在每次渲染时现算 —— `Date.now()` 在走而 `serverNow` 冻在上次请求，
 * 两边一起增长，这里一减就把流逝的时间抵消掉了，数字永远停在原地。
 * 客户报的「已工作时间不会变」就是它。
 *
 * 只显示到分钟。秒级跳动既费电，也会给人「工资按秒算」的错觉。
 */
function ShiftTimer({
  startedAt,
  clockSkewMs,
  pendingSync,
}: {
  startedAt: string;
  clockSkewMs: number;
  pendingSync: boolean;
}) {
  const { t } = useTranslation();
  const [, tick] = useState(0);

  useEffect(() => {
    // 半分钟一跳。只显示到分钟，所以最慢也就差半分钟
    const id = window.setInterval(() => tick((n) => n + 1), 30_000);
    const onVisible = () => {
      if (!document.hidden) tick((n) => n + 1);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // Math.max 兜住时钟倒退导致的负数
  const minutes = Math.max(
    0,
    Math.floor((Date.now() - clockSkewMs - Date.parse(startedAt)) / 60_000),
  );

  return (
    <div className="mb-4 flex items-baseline justify-between rounded-xl border border-brand-200 bg-brand-50 px-4 py-3">
      <span className="text-body text-ink-2">
        {t('punch.onTheClock')}
        {pendingSync && <span className="ml-2 text-caption text-ink-3">{t('punch.pendingSync')}</span>}
      </span>
      <span className="font-mono text-figure font-semibold tabular-nums text-brand-700">
        {String(Math.floor(minutes / 60)).padStart(2, '0')}:{String(minutes % 60).padStart(2, '0')}
      </span>
    </div>
  );
}

function LocationStatus({
  site,
  fix,
  distance,
  inRange,
  locating,
  onRefresh,
}: {
  site: Site | null;
  fix: Fix | null;
  distance: number | null;
  inRange: boolean;
  locating: boolean;
  onRefresh: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-title font-semibold">{site?.name}</p>
          {locating && <p className="mt-1 text-body text-ink-3">{t('punch.locating')}</p>}
          {!locating && distance != null && (
            <p className={`mt-1 text-body font-medium ${inRange ? 'text-ok' : 'text-danger'}`}>
              {t('punch.distanceFromSite', { distance: Math.round(distance) })}
              {fix?.accuracyM != null && (
                <span className="ml-1 font-normal text-ink-3">
                  ±{Math.round(fix.accuracyM)}m
                </span>
              )}
            </p>
          )}
          {!locating && !fix && <p className="mt-1 text-body text-ink-3">{t('punch.noLocation')}</p>}
          {site && (
            <p className="mt-0.5 font-mono text-caption text-ink-3">
              {t('punch.allowedRange', { radius: site.geofenceRadiusM })}
            </p>
          )}
        </div>

        <Button size="sm" variant="secondary" onClick={onRefresh} disabled={locating} className="shrink-0">
          {t('punch.refreshLocation')}
        </Button>
      </div>
    </Card>
  );
}

function TodayList({ siteId }: { siteId: number }) {
  const { t, i18n } = useTranslation();
  const today = useQuery({
    queryKey: ['punches-today', siteId],
    queryFn: () => attendanceApi.today(siteId!),
    enabled: siteId !== null,
  });

  if (!today.data || today.data.punches.length === 0) return null;
  const timeZone = today.data.timezone;
  const todayKey = dayKeyInTimeZone(new Date(), timeZone);

  return (
    <ListGroup title={t('punch.todayRecords')} className="mt-6">
      {today.data.punches.map((p) => (
        <ListRow
          key={p.id}
          title={p.type === PunchType.IN ? t('punch.clockIn') : t('punch.clockOut')}
          subtitle={
            <span className="tabular-nums">
              {/* 夜班过了零点，昨晚那张上班卡也在这里（CR-066）：不是今天的写上日期 */}
              {dayKeyInTimeZone(new Date(p.occurredAt), timeZone) !== todayKey &&
                `${new Date(p.occurredAt).toLocaleDateString(i18n.language, {
                  month: 'numeric',
                  day: 'numeric',
                  timeZone,
                })} `}
              {new Date(p.occurredAt).toLocaleTimeString(i18n.language, {
                hour: '2-digit',
                minute: '2-digit',
                timeZone,
              })}
            </span>
          }
          trailing={<StatusPill status={p.status} />}
        />
      ))}
    </ListGroup>
  );
}

function StatusPill({ status }: { status: string }) {
  const { t } = useTranslation();
  const map: Record<string, { tone: 'ok' | 'warn' | 'danger'; key: string }> = {
    APPROVED: { tone: 'ok', key: 'punch.statusApproved' },
    PENDING_REVIEW: { tone: 'warn', key: 'punch.statusPending' },
    REJECTED: { tone: 'danger', key: 'punch.statusRejected' },
  };
  const item = map[status] ?? map.APPROVED!;
  return <Badge tone={item.tone}>{t(item.key)}</Badge>;
}
